import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import Process from '../components/Process';
import CallToAction from '../components/CallToAction';
import PillarIcon from '../components/PillarIcon';
import Section from '../components/ui/Section';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';
import { ArrowRight, CheckIcon } from '../components/ui/Icons';
import usePageMeta from '../hooks/usePageMeta';
import pages from '../content/pages.json';
import { postsForTags } from '../data/blog';
import { pillars, secondaryOffers, stackGroups } from '../data/services';

const JumpList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 44px;
    padding: 0 1rem;
    border-radius: var(--radius-pill);
    border: 1px solid var(--border-strong);
    background: var(--surface);
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-1);
    transition: background-color 0.18s ease;
  }

  a:hover {
    background: var(--surface-hover);
  }

  svg {
    color: var(--accent);
  }
`;

const Pillar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(1.5rem, 5vw, 4rem);
  padding: clamp(2rem, 4vw, 3rem) 0;
  border-bottom: 1px solid var(--border);
  scroll-margin-top: calc(var(--nav-h) + 1rem);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PillarHead = styled.div`
  h2 {
    font-size: clamp(1.5rem, 2.4vw, 2rem);
    letter-spacing: -0.015em;
    margin: 1rem 0 0.75rem;
  }

  p {
    color: var(--text-2);
    max-width: 46ch;
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(192, 204, 48, 0.1);
  border: 1px solid rgba(192, 204, 48, 0.25);
  color: var(--accent);
`;

const Related = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 44px;
  margin-top: 1rem;
  color: var(--accent);
  font-weight: 600;
  font-size: 0.95rem;
`;

const FromBlog = styled.div`
  margin-top: 1.25rem;

  p {
    font-family: var(--font-body);
    font-size: var(--fs-eyebrow);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-3);
    max-width: none;
  }

  li a {
    display: inline-flex;
    align-items: center;
    min-height: 40px;
    color: var(--text-2);
    font-size: 0.95rem;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  li a:hover {
    color: var(--text-1);
  }
`;

const Bullets = styled.ul`
  display: grid;
  gap: 0.75rem;
  align-content: start;
  padding: 1.5rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);

  li {
    display: flex;
    gap: 0.75rem;
    color: var(--text-1);
    font-size: 0.975rem;
  }

  svg {
    margin-top: 0.3rem;
    color: var(--accent);
  }
`;

const OfferGrid = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const OfferCard = styled.div`
  height: 100%;
  padding: 1.75rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-2);
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }

  li {
    display: flex;
    gap: 0.6rem;
    font-size: 0.9rem;
    color: var(--text-2);
    padding: 0.25rem 0;
  }

  svg {
    margin-top: 0.25rem;
    color: var(--accent);
  }
`;

const StackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 440px) {
    grid-template-columns: 1fr;
  }

  h3 {
    font-family: var(--font-body);
    font-size: var(--fs-eyebrow);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-3);
    margin-bottom: 0.75rem;
  }

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  li {
    padding: 0.3rem 0.6rem;
    border-radius: 6px;
    font-size: 0.8rem;
    color: var(--text-1);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border);
  }
`;

const Services: React.FC = () => {
  usePageMeta(pages.services);

  return (
    <Layout>
      <PageHero
        eyebrow="Services"
        title="What we build, secure, and run."
        lead="Custom websites, mobile apps, cloud infrastructure, and cybersecurity, designed, built, secured, and maintained by one accountable team, so your project never gets handed off to three different vendors."
      >
        <nav aria-label="Service areas">
          <JumpList>
            {pillars.map((pillar) => (
              <li key={pillar.id}>
                <a href={`#${pillar.id}`}>
                  <PillarIcon id={pillar.id} size={18} />
                  {pillar.shortTitle}
                </a>
              </li>
            ))}
          </JumpList>
        </nav>
      </PageHero>

      <Section $tone="raised" aria-label="Core services">
        <Container>
          {pillars.map((pillar) => {
            const related = postsForTags(pillar.blogTags);
            return (
              <Pillar key={pillar.id} id={pillar.id}>
                <PillarHead>
                  <IconWrap>
                    <PillarIcon id={pillar.id} />
                  </IconWrap>
                  <h2>{pillar.title}</h2>
                  <p>{pillar.blurb}</p>
                  <Related to={`/projects#${pillar.relatedProject}`}>
                    Related work: {pillar.relatedLabel} <ArrowRight size={16} />
                  </Related>
                  {related.length > 0 && (
                    <FromBlog>
                      <p>From the blog</p>
                      <ul aria-label={`From the blog: ${pillar.title}`}>
                        {related.map((post) => (
                          <li key={post.slug}>
                            <Link to={`/blog/${post.slug}`} lang={post.lang === 'es' ? 'es' : undefined}>
                              {post.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </FromBlog>
                  )}
                </PillarHead>
                <Bullets aria-label={`${pillar.title} scope`}>
                  {pillar.bullets.map((bullet) => (
                    <li key={bullet}>
                      <CheckIcon />
                      {bullet}
                    </li>
                  ))}
                </Bullets>
              </Pillar>
            );
          })}
        </Container>
      </Section>

      <Section aria-labelledby="also-title">
        <Container>
          <SectionHeading
            id="also-title"
            eyebrow="Also"
            title="More ways we help"
            description="Often part of a bigger engagement, available on their own too."
          />
          <OfferGrid>
            {secondaryOffers.map((offer, index) => (
              <Reveal as="li" key={offer.title} delay={index * 0.06}>
                <OfferCard>
                  <h3>{offer.title}</h3>
                  <p>{offer.blurb}</p>
                  <ul>
                    {offer.bullets.map((bullet) => (
                      <li key={bullet}>
                        <CheckIcon size={14} />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </OfferCard>
              </Reveal>
            ))}
          </OfferGrid>
        </Container>
      </Section>

      <Process />

      <Section aria-labelledby="stack-title">
        <Container>
          <SectionHeading
            id="stack-title"
            eyebrow="Stack"
            title="The tools we reach for"
            description="We pick the stack that fits your project and your team. These are the ones we use most."
          />
          <StackGrid>
            {stackGroups.map((group) => (
              <div key={group.label}>
                <h3>{group.label}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </StackGrid>
        </Container>
      </Section>

      <CallToAction />
    </Layout>
  );
};

export default Services;
